import React, { useEffect, useState, useMemo } from 'react';
import client from '../api/client';

const PRIMARY = '#B71530';

// All endpoints declared in the app — used to show 0-hit rows for untracked endpoints
const ALL_ENDPOINTS = [
  // Auth
  { method: 'POST',  endpoint: '/api/auth/login',            group: 'Auth',            desc: 'Login' },
  { method: 'GET',   endpoint: '/api/auth/me',               group: 'Auth',            desc: 'Current user' },
  { method: 'PATCH', endpoint: '/api/auth/me',               group: 'Auth',            desc: 'Update profile / password' },
  { method: 'POST',  endpoint: '/api/auth/logout',           group: 'Auth',            desc: 'Logout' },
  // Dashboard
  { method: 'GET',   endpoint: '/api/dashboard/stats',       group: 'Dashboard',       desc: 'Stats (supports ?org_id)' },
  { method: 'GET',   endpoint: '/api/dashboard/status',      group: 'Dashboard',       desc: 'Pipeline status' },
  { method: 'POST',  endpoint: '/api/dashboard/start',       group: 'Dashboard',       desc: 'Start pipeline' },
  { method: 'POST',  endpoint: '/api/dashboard/stop',        group: 'Dashboard',       desc: 'Stop pipeline' },
  { method: 'GET',   endpoint: '/api/dashboard/new-since-last-visit', group: 'Dashboard', desc: 'New items since last login' },
  // Invoices
  { method: 'GET',   endpoint: '/api/invoices/',             group: 'Invoices',        desc: 'List invoices (paginated)' },
  { method: 'GET',   endpoint: '/api/invoices/{invoice_id}', group: 'Invoices',        desc: 'Invoice detail' },
  { method: 'PATCH', endpoint: '/api/invoices/{invoice_id}/status', group: 'Invoices', desc: 'Update invoice status' },
  { method: 'GET',   endpoint: '/api/invoices/{invoice_id}/pdf', group: 'Invoices',    desc: 'Download PDF' },
  // Exceptions
  { method: 'GET',   endpoint: '/api/exceptions/',           group: 'Exceptions',      desc: 'List exceptions' },
  { method: 'GET',   endpoint: '/api/exceptions/stats',      group: 'Exceptions',      desc: 'Exception stats' },
  { method: 'PATCH', endpoint: '/api/exceptions/{exception_id}/resolve', group: 'Exceptions', desc: 'Resolve' },
  { method: 'PATCH', endpoint: '/api/exceptions/{exception_id}/dismiss', group: 'Exceptions', desc: 'Dismiss' },
  // Reconciliation
  { method: 'GET',   endpoint: '/api/reconciliation/',       group: 'Reconciliation',  desc: 'Results' },
  { method: 'GET',   endpoint: '/api/reconciliation/stats',  group: 'Reconciliation',  desc: 'Stats' },
  { method: 'GET',   endpoint: '/api/reconciliation/unmatched-invoices', group: 'Reconciliation', desc: 'Unmatched invoices' },
  { method: 'GET',   endpoint: '/api/reconciliation/unmatched-sage',     group: 'Reconciliation', desc: 'Unmatched Sage creditors' },
  // Suppliers
  { method: 'GET',   endpoint: '/api/suppliers/',            group: 'Suppliers',       desc: 'List suppliers' },
  { method: 'POST',  endpoint: '/api/suppliers/',            group: 'Suppliers',       desc: 'Create supplier' },
  { method: 'PATCH', endpoint: '/api/suppliers/{supplier_id}', group: 'Suppliers',     desc: 'Update supplier' },
  { method: 'POST',  endpoint: '/api/suppliers/match',       group: 'Suppliers',       desc: 'Run matching' },
  // Credit Notes
  { method: 'GET',   endpoint: '/api/credit-notes/',         group: 'Credit Notes',    desc: 'List credit notes' },
  { method: 'PATCH', endpoint: '/api/credit-notes/{credit_note_id}/allocate', group: 'Credit Notes', desc: 'Allocate' },
  // Statements
  { method: 'GET',   endpoint: '/api/statements/',           group: 'Statements',      desc: 'Statements & remittances' },
  // Processing Runs
  { method: 'GET',   endpoint: '/api/processing-runs/',      group: 'Processing',      desc: 'Run history' },
  // Settings
  { method: 'GET',   endpoint: '/api/settings/config',       group: 'Settings',        desc: 'Get config' },
  { method: 'PATCH', endpoint: '/api/settings/config',       group: 'Settings',        desc: 'Update config' },
  { method: 'POST',  endpoint: '/api/settings/sage-import',  group: 'Settings',        desc: 'Import Sage file' },
  { method: 'POST',  endpoint: '/api/settings/syrinx-import', group: 'Settings',       desc: 'Import Syrinx file' },
  { method: 'POST',  endpoint: '/api/settings/run-matching', group: 'Settings',        desc: 'Run matching' },
  { method: 'GET',   endpoint: '/api/settings/users',        group: 'Settings',        desc: 'List org users' },
  { method: 'POST',  endpoint: '/api/settings/users',        group: 'Settings',        desc: 'Create org user' },
  { method: 'PATCH', endpoint: '/api/settings/users/{user_id}', group: 'Settings',     desc: 'Update org user' },
  { method: 'DELETE',endpoint: '/api/settings/users/{user_id}', group: 'Settings',     desc: 'Deactivate org user' },
  // Organizations (App Admin)
  { method: 'GET',   endpoint: '/api/orgs/branding',         group: 'Organizations',   desc: 'Get branding (public)' },
  { method: 'GET',   endpoint: '/api/orgs/',                 group: 'Organizations',   desc: 'List all orgs' },
  { method: 'POST',  endpoint: '/api/orgs/',                 group: 'Organizations',   desc: 'Create org' },
  { method: 'PATCH', endpoint: '/api/orgs/{org_id}',         group: 'Organizations',   desc: 'Update org' },
  { method: 'POST',  endpoint: '/api/orgs/{org_id}/logo',    group: 'Organizations',   desc: 'Upload logo' },
  { method: 'POST',  endpoint: '/api/orgs/{org_id}/favicon', group: 'Organizations',   desc: 'Upload favicon' },
  { method: 'GET',   endpoint: '/api/orgs/{org_id}/users',   group: 'Organizations',   desc: 'List org users' },
  { method: 'POST',  endpoint: '/api/orgs/{org_id}/users',   group: 'Organizations',   desc: 'Create user in org' },
  { method: 'PATCH', endpoint: '/api/orgs/{org_id}/users/{user_id}', group: 'Organizations', desc: 'Reset PW / toggle active / role' },
  { method: 'GET',   endpoint: '/api/orgs/{org_id}/invites', group: 'Organizations',   desc: 'List invites' },
  { method: 'POST',  endpoint: '/api/orgs/{org_id}/invites', group: 'Organizations',   desc: 'Create invite' },
  { method: 'DELETE',endpoint: '/api/orgs/{org_id}/invites/{invite_id}', group: 'Organizations', desc: 'Cancel invite' },
  { method: 'GET',   endpoint: '/api/orgs/all-users',        group: 'Organizations',   desc: 'All users across all orgs' },
  { method: 'GET',   endpoint: '/api/orgs/usage',            group: 'Organizations',   desc: 'Usage stats per org' },
  { method: 'GET',   endpoint: '/api/orgs/{org_id}/logs',    group: 'Organizations',   desc: 'Processing logs for org' },
  { method: 'GET',   endpoint: '/api/orgs/api-stats',        group: 'Organizations',   desc: 'API hit stats' },
  // Maintenance
  { method: 'GET',   endpoint: '/api/maintenance/status',    group: 'Maintenance',     desc: 'Maintenance status (public)' },
  { method: 'PATCH', endpoint: '/api/maintenance/',          group: 'Maintenance',     desc: 'Toggle maintenance mode' },
  // Monitoring
  { method: 'GET',   endpoint: '/api/health',                group: 'Monitoring',      desc: 'Health check' },
  { method: 'GET',   endpoint: '/api/metrics/business',      group: 'Monitoring',      desc: 'Business metrics for Grafana' },
];

const METHOD_COLORS = {
  GET:    { color: '#0d6efd', bg: '#cfe2ff' },
  POST:   { color: '#198754', bg: '#d1e7dd' },
  PATCH:  { color: '#fd7e14', bg: '#ffe5d0' },
  DELETE: { color: '#dc3545', bg: '#f8d7da' },
  PUT:    { color: '#6f42c1', bg: '#e2d9f3' },
};

const GROUP_ORDER = [
  'Dashboard', 'Invoices', 'Exceptions', 'Reconciliation', 'Suppliers',
  'Credit Notes', 'Statements', 'Processing', 'Settings',
  'Auth', 'Organizations', 'Maintenance', 'Monitoring',
];

function MethodBadge({ method }) {
  const cfg = METHOD_COLORS[method] || { color: '#6c757d', bg: '#e2e3e5' };
  return (
    <span style={{
      display: 'inline-block', padding: '1px 7px', borderRadius: '4px',
      fontSize: '10px', fontWeight: 700, letterSpacing: '0.5px',
      backgroundColor: cfg.bg, color: cfg.color, fontFamily: 'monospace',
      minWidth: '52px', textAlign: 'center',
    }}>
      {method}
    </span>
  );
}

export default function AppAdminApi() {
  const [stats, setStats]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [days, setDays]       = useState(30);
  const [search, setSearch]   = useState('');
  const [groupFilter, setGroupFilter] = useState('All');
  const [expandedGroups, setExpandedGroups] = useState(new Set(GROUP_ORDER));
  const [orgFilter, setOrgFilter]           = useState('__all__');
  const [orgList, setOrgList]               = useState([]);  // from /api/orgs/

  // Load orgs once for the filter dropdown — independent of hit data
  useEffect(() => {
    client.get('/orgs/')
      .then(r => setOrgList(r.data.organizations || []))
      .catch(() => {});
  }, []);

  const fetchStats = () => {
    setLoading(true);
    client.get(`/orgs/api-stats?days=${days}`)
      .then(r => setStats(r.data.stats || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchStats(); }, [days]);

  // Org names seen in hit data (for table columns)
  const allOrgs = useMemo(() => {
    const s = new Set();
    stats.forEach(r => {
      if (r.org_name && r.org_name !== 'Frontend (pre-login)' && r.org_name !== 'App Admin') {
        s.add(r.org_name);
      }
    });
    return [...s].sort();
  }, [stats]);

  // Build a lookup: "METHOD::endpoint" → { total (filtered), orgs: { name → count } }
  const hitMap = useMemo(() => {
    const map = {};
    const activeRows = orgFilter === '__all__'
      ? stats
      : stats.filter(r => r.org_name === orgFilter);

    for (const row of activeRows) {
      const key = `${row.method}::${row.endpoint}`;
      if (!map[key]) map[key] = { total: 0, orgs: {} };
      map[key].total += row.total_hits;
      map[key].orgs[row.org_name] = (map[key].orgs[row.org_name] || 0) + row.total_hits;
    }
    return map;
  }, [stats, orgFilter]);

  // When filtered to one org, only that column is shown; otherwise show all
  const visibleOrgs = orgFilter === '__all__' ? allOrgs : allOrgs.filter(o => o === orgFilter);

  const groups = useMemo(() => {
    const order = [...GROUP_ORDER, ...new Set(ALL_ENDPOINTS.map(e => e.group)).values()
      .filter(g => !GROUP_ORDER.includes(g))];
    return ['All', ...order.filter(g => ALL_ENDPOINTS.some(e => e.group === g))];
  }, []);

  const filtered = useMemo(() => {
    return ALL_ENDPOINTS.filter(e =>
      (groupFilter === 'All' || e.group === groupFilter) &&
      (!search || e.endpoint.includes(search) || e.desc.toLowerCase().includes(search.toLowerCase()))
    );
  }, [groupFilter, search]);

  // Group filtered endpoints
  const grouped = useMemo(() => {
    const map = {};
    for (const e of filtered) {
      if (!map[e.group]) map[e.group] = [];
      map[e.group].push(e);
    }
    return map;
  }, [filtered]);

  const totalHits = (orgFilter === '__all__' ? stats : stats.filter(r => r.org_name === orgFilter))
    .reduce((s, r) => s + r.total_hits, 0);

  const toggleGroup = (g) =>
    setExpandedGroups(prev => {
      const next = new Set(prev);
      next.has(g) ? next.delete(g) : next.add(g);
      return next;
    });

  return (
    <div>
      {/* Controls */}
      <div className="card mb-3" style={{ borderLeft: `4px solid ${PRIMARY}` }}>
        <div className="card-body py-2 d-flex align-items-center flex-wrap" style={{ gap: '12px' }}>
          <i className="bi bi-activity" style={{ fontSize: '16px', color: PRIMARY }} />
          <span style={{ fontSize: '13px', fontWeight: 600 }}>API Usage</span>

          <select className="form-control form-control-sm" style={{ width: '140px' }}
            value={days} onChange={e => setDays(Number(e.target.value))}>
            <option value={1}>Last 24 hours</option>
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
          </select>

          {/* Org filter */}
          <div className="d-flex align-items-center" style={{ gap: '6px' }}>
            <i className="bi bi-diagram-3" style={{ fontSize: '14px', color: '#6c757d' }} />
            <select
              className="form-control form-control-sm"
              style={{
                width: '190px',
                borderColor: orgFilter !== '__all__' ? PRIMARY : '#ced4da',
                fontWeight: orgFilter !== '__all__' ? 600 : 400,
                color: orgFilter !== '__all__' ? PRIMARY : 'inherit',
              }}
              value={orgFilter}
              onChange={e => setOrgFilter(e.target.value)}
            >
              <option value="__all__">All Organisations</option>
              {orgList.map(o => (
                <option key={o.id} value={o.name}>
                  {o.name} ({o.subdomain})
                </option>
              ))}
            </select>
            {orgFilter !== '__all__' && (
              <button
                onClick={() => setOrgFilter('__all__')}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6c757d', fontSize: '14px', padding: '0 2px' }}
                title="Clear filter"
              >×</button>
            )}
          </div>

          <input type="text" className="form-control form-control-sm"
            style={{ width: '220px' }} placeholder="Search endpoint or description…"
            value={search} onChange={e => setSearch(e.target.value)} />

          <div className="d-flex" style={{ gap: '4px', flexWrap: 'wrap' }}>
            {groups.map(g => (
              <button key={g} onClick={() => setGroupFilter(g)}
                style={{
                  padding: '2px 10px', border: '1px solid', borderRadius: '12px',
                  fontSize: '11px', cursor: 'pointer', fontWeight: groupFilter === g ? 600 : 400,
                  backgroundColor: groupFilter === g ? PRIMARY : '#fff',
                  color: groupFilter === g ? '#fff' : '#495057',
                  borderColor: groupFilter === g ? PRIMARY : '#dee2e6',
                }}>
                {g}
              </button>
            ))}
          </div>

          <div className="ml-auto d-flex align-items-center" style={{ gap: '10px' }}>
            <span style={{ fontSize: '12px', color: '#6c757d' }}>
              <strong style={{ color: '#212529' }}>{totalHits.toLocaleString()}</strong> total hits
            </span>
            <button className="btn btn-sm btn-outline-secondary" style={{ fontSize: '12px' }}
              onClick={fetchStats} disabled={loading}>
              <i className="bi bi-arrow-clockwise mr-1" />Refresh
            </button>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-5"><div className="spinner-border text-danger" /></div>
      ) : (
        Object.entries(grouped)
          .sort(([a], [b]) => GROUP_ORDER.indexOf(a) - GROUP_ORDER.indexOf(b))
          .map(([group, endpoints]) => {
            const groupHits = endpoints.reduce((s, e) => {
              const data = hitMap[`${e.method}::${e.endpoint}`];
              return s + (orgFilter === '__all__' ? (data?.total || 0) : (data?.orgs?.[orgFilter] || 0));
            }, 0);
            const isOpen = expandedGroups.has(group);

            return (
              <div className="card mb-2" key={group}>
                {/* Group header */}
                <div
                  className="card-header d-flex align-items-center justify-content-between py-2"
                  style={{ backgroundColor: '#f8f9fa', cursor: 'pointer', userSelect: 'none' }}
                  onClick={() => toggleGroup(group)}
                >
                  <div className="d-flex align-items-center" style={{ gap: '10px' }}>
                    <i className={`bi bi-chevron-${isOpen ? 'down' : 'right'}`}
                      style={{ fontSize: '11px', color: '#6c757d' }} />
                    <span style={{ fontWeight: 600, fontSize: '13px' }}>{group}</span>
                    <span className="badge badge-secondary" style={{ fontWeight: 400, fontSize: '11px' }}>
                      {endpoints.length} endpoint{endpoints.length !== 1 ? 's' : ''}
                    </span>
                  </div>
                  {groupHits > 0 && (
                    <span style={{ fontSize: '12px', color: PRIMARY, fontWeight: 600 }}>
                      {groupHits.toLocaleString()} hits
                    </span>
                  )}
                </div>

                {/* Endpoint rows */}
                {isOpen && (
                  <div className="card-body p-0">
                    <table className="table table-sm table-hover mb-0" style={{ fontSize: '12px' }}>
                      <thead className="table-light">
                        <tr>
                          <th style={{ width: '70px' }}>Method</th>
                          <th>Endpoint</th>
                          <th>Description</th>
                          <th style={{ width: '90px', textAlign: 'right' }}>
                            {orgFilter === '__all__' ? 'Total Hits' : `Hits — ${orgFilter}`}
                          </th>
                          {orgFilter === '__all__' && allOrgs.map(org => (
                            <th key={org} style={{ width: '90px', textAlign: 'right', maxWidth: '90px' }}>
                              <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '11px' }}
                                title={org}>
                                {org}
                              </div>
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {endpoints.map(e => {
                          const key   = `${e.method}::${e.endpoint}`;
                          const data  = hitMap[key];
                          const total = data?.total || 0;
                          return (
                            <tr key={key}
                              style={{ backgroundColor: total > 0 ? 'inherit' : '#fafafa' }}>
                              <td><MethodBadge method={e.method} /></td>
                              <td>
                                <code style={{ fontSize: '11px', color: '#212529' }}>
                                  {e.endpoint}
                                </code>
                              </td>
                              <td style={{ color: '#6c757d' }}>{e.desc}</td>
                              <td style={{ textAlign: 'right', fontWeight: total > 0 ? 600 : 400,
                                color: total > 0 ? '#212529' : '#adb5bd' }}>
                                {total > 0 ? total.toLocaleString() : '–'}
                              </td>
                              {orgFilter === '__all__' && allOrgs.map(org => {
                                const c = data?.orgs?.[org] || 0;
                                return (
                                  <td key={org} style={{ textAlign: 'right',
                                    color: c > 0 ? '#212529' : '#adb5bd', fontWeight: c > 0 ? 500 : 400 }}>
                                    {c > 0 ? c.toLocaleString() : '–'}
                                  </td>
                                );
                              })}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })
      )}
    </div>
  );
}
